import { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TreeList } from "@astryxdesign/core/TreeList";
import { Text } from "@astryxdesign/core/Text";
import { EmptyState } from "@astryxdesign/core/EmptyState";

/**
 * Constrói os itens do TreeList a partir da estrutura recursiva de pastas.
 * A expansão inicial segue exatamente o caminho atual; nós fora do caminho
 * começam recolhidos.
 */
function buildItems(children, currentPath, onFolderSelect, depth = 0) {
  if (!children) return [];
  return Object.entries(children).map(([name, node]) => {
    const hasChildren =
      node.children && Object.keys(node.children).length > 0;
    const isFolder = node.type === "folder";
    const inPath = currentPath[depth] === name;

    return {
      id: node.id,
      label: name,
      startContent: (
        <Text color={isFolder ? "accent" : "secondary"}>
          <FontAwesomeIcon icon={isFolder ? "folder" : "file-alt"} />
        </Text>
      ),
      endContent:
        isFolder && hasChildren ? (
          <Text type="supporting">{Object.keys(node.children).length}</Text>
        ) : undefined,
      isExpanded: inPath && hasChildren,
      isSelected: inPath && depth === currentPath.length - 1,
      onClick: () => onFolderSelect(name, node.originalName || name),
      children: hasChildren
        ? buildItems(
            node.children,
            inPath ? currentPath : [],
            onFolderSelect,
            depth + 1,
          )
        : undefined,
    };
  });
}

/** Árvore de pastas do bucket (adaptador da estrutura para o TreeList do Astryx). */
export default function FolderTree({
  fileStructure,
  onFolderSelect,
  currentPath = [],
}) {
  const items = useMemo(
    () => buildItems(fileStructure?.children, currentPath, onFolderSelect),
    [fileStructure, currentPath, onFolderSelect],
  );

  if (!fileStructure || !fileStructure.children || items.length === 0) {
    return (
      <EmptyState
        title="Nenhuma pasta encontrada"
        icon={<FontAwesomeIcon icon="folder-open" />}
      />
    );
  }

  // A expansão do TreeList é interna (isExpanded só semeia o estado inicial);
  // a key remonta a árvore quando o caminho muda, fazendo a expansão refletir
  // o caminho atual — substitui o antigo flag resetExpansion.
  return (
    <TreeList
      key={currentPath.join("/")}
      items={items}
      density="compact"
    />
  );
}
